import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import Countdown from "@/components/Countdown";
import {
  BookOpen, Calendar, Clock3, CheckCircle2, Lock, Play, ExternalLink,
  Award, Video, ChevronRight, Sparkles, Radio,
} from "lucide-react";

const STATE_TINT = {
  before: { bg: "#F1F3F7", text: "#0B0D17", accent: "#0B5CFF", label: "Upcoming" },
  live:   { bg: "#FDECEC", text: "#B00020", accent: "#E02D3C", label: "Live now" },
  after:  { bg: "#E7F5EC", text: "#1E8E3E", accent: "#1E8E3E", label: "Completed" },
};

export default function CoursePortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/public/course-portal/${token}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Course portal not found");
    }
  };
  useEffect(() => { load(); /* refresh every 60s so live/unlock states update */
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="vp-card p-8 max-w-md text-center">
        <div className="vp-label mb-2">Course Portal</div>
        <div className="font-display text-2xl font-medium">This link isn't active.</div>
        <p className="text-[#4A4D57] mt-2 text-sm">{err}</p>
        <Link to="/" className="vp-btn-ghost mt-6 inline-flex">Go home</Link>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;

  const { enrollment, course, host, modules, stats } = data;
  const nextUpcoming = modules.find((m) => m.state === "before");

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="max-w-[1200px] mx-auto flex items-center justify-between px-6 sm:px-10 py-6" data-testid="course-portal-nav">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#F6F7FA] border border-[#0B5CFF]/40 flex items-center justify-center">
            <Video className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
          </div>
          <span className="font-display font-medium text-lg tracking-tight">Videoepreneur</span>
        </Link>
        <span className="vp-chip inline-flex items-center gap-1.5" style={{ color: "#0B5CFF", borderColor: "#0B5CFF60" }}>
          <BookOpen className="w-3.5 h-3.5" /> Course portal
        </span>
      </nav>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-4 pb-10">
        <div className="vp-label">Welcome back</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.05] tracking-[-0.028em] mt-2">
          Hi <span className="font-serif-italic text-[#0B5CFF]">{enrollment.name.split(" ")[0]}</span> — here's your progress on
        </h1>
        <div className="font-display text-2xl sm:text-3xl font-bold mt-2" data-testid="course-portal-title">{course.title}</div>
        <p className="text-[#4A4D57] mt-3 max-w-2xl text-base">{course.description}</p>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
          <div className="vp-kpi" data-testid="portal-kpi-progress">
            <div className="vp-label">Progress</div>
            <div className="font-display text-2xl font-bold mt-1">{stats.progress_pct}%</div>
          </div>
          <div className="vp-kpi" data-testid="portal-kpi-attended">
            <div className="vp-label">Attended</div>
            <div className="font-display text-2xl font-bold mt-1">{stats.attended} / {stats.total}</div>
          </div>
          <div className="vp-kpi" data-testid="portal-kpi-unlocked">
            <div className="vp-label">Unlocked</div>
            <div className="font-display text-2xl font-bold mt-1">{stats.unlocked}</div>
          </div>
          <div className="vp-kpi" data-testid="portal-kpi-live">
            <div className="vp-label">Live now</div>
            <div className="font-display text-2xl font-bold mt-1 inline-flex items-center gap-2">
              {stats.live_now}
              {stats.live_now > 0 && <Radio className="w-4 h-4 text-[#E02D3C] animate-pulse" />}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="w-full h-2 rounded-full bg-[#E4E6EC] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#0B5CFF] to-[#7C3AED] transition-all duration-500" style={{ width: `${stats.progress_pct}%` }} />
          </div>
        </div>
      </section>

      {/* Next up */}
      {nextUpcoming && (
        <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pb-6">
          <div className="vp-glass p-6 flex flex-col md:flex-row md:items-center gap-6 justify-between" data-testid="portal-next-up">
            <div>
              <div className="vp-label" style={{ color: "#0B5CFF" }}>Next up</div>
              <div className="font-display font-bold text-xl mt-1">{nextUpcoming.title}</div>
              <div className="text-sm text-[#4A4D57] mt-1 inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> {new Date(nextUpcoming.scheduled_at).toLocaleString("en-IN")}
              </div>
            </div>
            <div>
              <div className="vp-label mb-2 text-right">Starts in</div>
              <Countdown target={nextUpcoming.scheduled_at} />
            </div>
          </div>
        </section>
      )}

      {/* Modules list */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pb-24">
        <div className="vp-label mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#0B5CFF]" /> All modules</div>
        <div className="space-y-3">
          {modules.length === 0 && (
            <div className="vp-card p-6 text-[#4A4D57]">This course has no modules yet. Check back soon.</div>
          )}
          {modules.map((m, i) => {
            const tint = STATE_TINT[m.state];
            return (
              <div
                key={m.id}
                className="vp-card p-5 flex flex-col md:flex-row md:items-center gap-4"
                data-testid="portal-module-row"
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: tint.bg, color: tint.accent }}>
                    {m.attended ? <CheckCircle2 className="w-5 h-5" /> : (m.unlocked ? <Play className="w-5 h-5" /> : <Lock className="w-5 h-5" />)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-[#747680] font-mono">MODULE {String(i + 1).padStart(2, "0")}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border" style={{ color: tint.accent, borderColor: tint.accent + "60", background: tint.bg }}>
                        {tint.label}
                      </span>
                      {m.attended && (
                        <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E7F5EC] text-[#1E8E3E] inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Attended
                        </span>
                      )}
                    </div>
                    <div className="font-display font-bold text-lg mt-1">{m.title}</div>
                    {m.description && (
                      <div className="text-sm text-[#4A4D57] mt-1 line-clamp-2">{m.description}</div>
                    )}
                    <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-[#747680]">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(m.scheduled_at).toLocaleString("en-IN")}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock3 className="w-3 h-3" /> {m.duration_min} min</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 md:justify-end shrink-0">
                  {!m.unlocked && (
                    <div className="text-xs text-[#747680] inline-flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" /> Unlocks {new Date(m.scheduled_at).toLocaleDateString("en-IN")}
                    </div>
                  )}
                  {m.unlocked && m.state !== "after" && m.join_url && (
                    <a
                      href={m.join_url}
                      target="_blank" rel="noreferrer"
                      className={"text-sm inline-flex items-center gap-2 " + (m.state === "live" ? "vp-btn-primary" : "vp-btn-ghost")}
                      data-testid={`portal-join-${m.id}`}
                    >
                      {m.state === "live" ? "Join live now" : "Join session"}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {m.state === "after" && m.recording_url && (
                    <a href={m.recording_url} target="_blank" rel="noreferrer" className="vp-btn-primary text-sm inline-flex items-center gap-2" data-testid={`portal-recording-${m.id}`}>
                      Watch recording <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {m.portal_token && m.state !== "before" && (
                    <Link to={`/portal/${m.portal_token}`} className="text-xs text-[#0B5CFF] hover:underline inline-flex items-center gap-1" data-testid={`portal-open-${m.id}`}>
                      Open <ChevronRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Host card */}
        <div className="vp-card p-6 mt-10" data-testid="portal-host-card">
          <div className="vp-label mb-3">Your instructor</div>
          <div className="font-display font-semibold text-lg">{host.name}</div>
          {host.host_bio && <p className="text-sm text-[#4A4D57] mt-1">{host.host_bio}</p>}
        </div>
      </section>

      <footer className="border-t border-[#E4E6EC] py-8 text-center text-sm text-[#747680]">
        <div>© {new Date().getFullYear()} Videoepreneur · <span className="opacity-70">Your course portal auto-updates every minute</span></div>
      </footer>
    </div>
  );
}
